import { ChangeDetectionStrategy, Component, booleanAttribute, computed, input } from '@angular/core';

import { MatIcon } from '@angular/material/icon';

import { SpinnerComponent } from './spinner.component';

const LIST_CLASS =
  'overflow-y-auto rounded-lg border border-neutral-200 bg-neutral-0 py-1 shadow-lg transition duration-150 ease-out motion-reduce:transition-none starting:-translate-y-1 starting:opacity-0';

/**
 * What a search answered with, under the field that asked. Rows are projected — every dictionary
 * reads differently — while having nothing to offer, and failing to ask at all, are stated here so
 * that a picker never renders an empty box the reader has to interpret.
 *
 * Anchored by the caller: `inline` where the list sits under a field in the page flow, off where an
 * overlay places it because the field sits in a container that would otherwise clip it.
 */
@Component({
  selector: 'guarantees-option-list',
  standalone: true,
  imports: [MatIcon, SpinnerComponent],
  // A box the caller can give a width to — left inline, a width set on the host would not apply.
  host: { class: 'block min-w-0' },
  template: `
    <ul [class]="listClass()">
      @if (error()) {
        <li class="flex items-center gap-1.5 px-2 py-1.5 text-sm text-error-700">
          <mat-icon class="h-4! w-4! shrink-0 text-base! leading-4!">sync_problem</mat-icon>
          {{ error() }}
        </li>
      }

      <ng-content />

      <!-- Above the rows, which may still hold the previous answer while a newer ask is in flight. -->
      @if (loading()) {
        <li class="flex items-center gap-1.5 px-2 py-1.5 text-sm text-neutral-400">
          <guarantees-spinner />
          {{ loadingMessage() }}
        </li>
      } @else if (empty()) {
        <li class="px-2 py-1.5 text-sm text-neutral-400">{{ emptyMessage() }}</li>
      }
    </ul>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OptionListComponent {
  /** Positions the list under the field itself. Off where an overlay already places it. */
  inline = input(false, { transform: booleanAttribute });
  /** How far the list may grow before it scrolls. */
  maxHeight = input<string>('max-h-64');
  /** Nothing matched. Kept off while a search is still running: no answer yet is not no answer. */
  empty = input(false, { transform: booleanAttribute });
  emptyMessage = input<string>('');
  /** An answer is in flight — stated in the list itself, and it silences `empty` so the two never fight. */
  loading = input(false, { transform: booleanAttribute });
  loadingMessage = input<string>('');
  /** Why there is nothing to show, where the search itself failed. */
  error = input<string>('');

  protected readonly listClass = computed(() => `${this.maxHeight()} ${LIST_CLASS} ${this.inline() ? 'absolute z-30 mt-1 w-full' : ''}`);
}
